import Async from "components/DesignSystem/AsyncComponents/Async";
import { Form, Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import isEqual from "lodash/isEqual";
import { useParams } from "react-router-dom";
import {
  useEditEntityDataAttributesMutation,
  useGetEntityDataAttributesQuery,
} from "store/apis/entity";
import { EntityDataAttribute } from "types/Models/entity";
import { BackendError } from "types/utils/error";
import { FieldBuilder } from "./FieldBuilder";
import Jurisdictions from "./Jurisdictions";
import { useMemo, useRef, useState } from "react";
import { EntityDataAttributeAccordion } from "./EntityDataAttributeAccordion";
import { Breifcase } from "components/icons/Breifcase";
import { ComplianceDoc } from "components/icons/ComplianceDoc";
import { FolderUpload } from "components/icons/FolderUpload";
import { PhonePlus } from "components/icons/PhonePlus";
import { Referral } from "components/icons/Referral";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { useModal } from "hooks/useModal";
import { EditEntityName } from "./EditEntityName";

const accordionDetails = {
  "Basic Information": {
    icon: <Breifcase />,
    title: "Business Details",
    subtitle: "Business identity and registration details",
  },
  "Contact and Addresses": {
    icon: <PhonePlus />,
    title: "Contact and Addresses",
    subtitle:
      "Official contact information and addresses for business correspondence",
  },
  "Tax and Compliance Information": {
    icon: <ComplianceDoc />,
    title: "Tax and Compliance Information",
    subtitle:
      "Key tax and regulatory details for federal compliance and classification",
  },
  "Entity Documents": {
    icon: <FolderUpload />,
    title: "Entity Documents",
    subtitle: "Documents regarding the business's legal and tax status",
  },
  "Equity and Shareholding Details": {
    icon: <Referral />,
    title: "Equity and Shareholding Details",
    subtitle:
      "Details of the company's equity distribution and shareholding structure",
  },
};

const fieldsToSkip = ["entity_name", "entity_country", "entity_formation_type"];
const basicInformation = "Basic Information";
const dateOfIncorporation = "date_of_incorporation";

type SectionFormProps = {
  sectionName: string;
  sectionDetails: { icon: React.ReactNode; title: string; subtitle: string };
  fields: EntityDataAttribute[];
  skippedFields: EntityDataAttribute[];
  isEditing: boolean;
  onEditClick: () => void;
  onExitEdit: () => void;
  isPreincorporated: boolean;
  groupId: string;
  entityId: string;
};

const SectionForm = ({
  sectionName,
  sectionDetails,
  fields,
  skippedFields,
  isEditing,
  onEditClick,
  onExitEdit,
  isPreincorporated,
  groupId,
  entityId,
}: SectionFormProps) => {
  const { successToast, alertToast } = useToast();
  const [editEntityDataAttributes] = useEditEntityDataAttributesMutation();
  const editEntityNameModal = useModal();
  const [hasChanges, setHasChanges] = useState(false);
  const pendingChanges = useRef<Record<string, EntityDataAttribute["value"]>>(
    {}
  );

  const isBasicInfo = sectionName === basicInformation;
  const allFields = isBasicInfo ? [...skippedFields, ...fields] : fields;
  const buildField = (field: EntityDataAttribute): EntityDataAttribute => ({
    ...field,
    disabled:
      field.disabled ||
      (field.name === dateOfIncorporation && Boolean(isPreincorporated)) ||
      !isEditing,
  });

  const initialValues = Object.fromEntries(
    allFields.map((field) => {
      const value =
        field.type === "PHONE" && typeof field.value === "string"
          ? field.value.replace(/^\+1\s?/, "")
          : field.value;
      return [field.name, value];
    })
  ) as Record<string, EntityDataAttribute["value"]>;

  const onChange = ({
    name,
    value,
  }: {
    name: string;
    value: EntityDataAttribute["value"];
  }) => {
    const isSameValue = isEqual(
      JSON.stringify(value),
      JSON.stringify(initialValues[name])
    );

    if (isSameValue) {
      delete pendingChanges.current[name];
    } else {
      pendingChanges.current[name] = value;
    }

    setHasChanges(Object.keys(pendingChanges.current).length > 0);
  };

  const onSave = async () => {
    const payload = pendingChanges.current;
    if (Object.keys(payload).length === 0) return;

    try {
      await editEntityDataAttributes({ groupId, entityId, payload }).unwrap();
      successToast({ message: "Entity updated successfully" });
      pendingChanges.current = {};
      setHasChanges(false);
      onExitEdit();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      enableReinitialize
    >
      {({ resetForm, setFieldValue, values }) => {
        const handleCancelEdit = () => {
          pendingChanges.current = {};
          resetForm();
          setHasChanges(false);
          onExitEdit();
        };
        return (
          <Form className="t-m-0">
            <EntityDataAttributeAccordion
              {...sectionDetails}
              content={fields.map((field) => (
                <FieldBuilder
                  field={buildField(field)}
                  size="regular"
                  onChange={onChange}
                  key={field.uuid}
                  disableDateTyping
                  withInkleDocs
                />
              ))}
              extraFields={skippedFields}
              onChange={onChange}
              onSave={onSave}
              editEntityName={
                isBasicInfo ? editEntityNameModal.open : undefined
              }
              isDisabled={!isEditing}
              hasChanges={hasChanges}
              onCancelEdit={handleCancelEdit}
              onEditClick={onEditClick}
            />
            {isBasicInfo && editEntityNameModal.isOpen && (
              <EditEntityName
                {...editEntityNameModal}
                entityName={(values["entity_name"] || "") as string}
                entityId={entityId}
                groupId={groupId}
                setFieldValue={setFieldValue}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

const EntityFormEngine = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const [openAccordions, setOpenAccordions] = useState<string[]>([
    "Business Details",
  ]);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionResetKeys, setSectionResetKeys] = useState<
    Record<string, number>
  >({});

  const {
    data: currentEntityDetails = [],
    isLoading,
    isSuccess,
  } = useGetEntityDataAttributesQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId || !entityId }
  );

  const fieldWithSection = currentEntityDetails
    .filter((field) => !fieldsToSkip.includes(field.name))
    .reduce((acc, field) => {
      if (field.section_name) {
        if (!acc[field.section_name]) {
          acc[field.section_name] = [];
        }
        acc[field.section_name].push(field);
      }
      return acc;
    }, {} as Record<string, EntityDataAttribute[]>);

  const skippedFields = useMemo(
    () =>
      currentEntityDetails.filter((field) => fieldsToSkip.includes(field.name)),
    [currentEntityDetails]
  );

  const isPreincorporated = Boolean(
    currentEntityDetails.find(
      (field) => field.name === "entity_status" && field.value === "0"
    )
  );

  const isEmpty = currentEntityDetails.length === 0;

  return (
    <Async.Root isEmpty={isEmpty} isLoading={isLoading} isSuccess={isSuccess}>
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <div className="t-space-y-5">
          <div className="t-flex t-gap-6 sm:t-flex-col">
            <div className="t-min-w-0 t-flex-1">
              <Accordion.Root
                type="multiple"
                value={openAccordions}
                onValueChange={setOpenAccordions}
                className="t-space-y-4"
              >
                {Object.keys(accordionDetails).map((sectionName) => {
                  const sectionDetails =
                    accordionDetails[
                      sectionName as keyof typeof accordionDetails
                    ];
                  const skippedSectionFields =
                    sectionName === basicInformation ? skippedFields : [];
                  const onEditClick = () => {
                    if (editingSection && editingSection !== sectionName) {
                      setSectionResetKeys((prev) => ({
                        ...prev,
                        [editingSection]: (prev[editingSection] ?? 0) + 1,
                      }));
                    }
                    setEditingSection(sectionName);
                    setOpenAccordions((prev) =>
                      prev.includes(sectionDetails.title)
                        ? prev
                        : [...prev, sectionDetails.title]
                    );
                  };

                  return (
                    <SectionForm
                      key={`${sectionName}-${
                        sectionResetKeys[sectionName] ?? 0
                      }`}
                      sectionName={sectionName}
                      sectionDetails={sectionDetails}
                      fields={fieldWithSection[sectionName] || []}
                      skippedFields={skippedSectionFields}
                      isEditing={editingSection === sectionName}
                      onEditClick={onEditClick}
                      onExitEdit={() => setEditingSection(null)}
                      isPreincorporated={isPreincorporated}
                      groupId={groupId!}
                      entityId={entityId}
                    />
                  );
                })}
              </Accordion.Root>
            </div>
            <div className="t-w-[300px] sm:t-w-full">
              <Jurisdictions />
            </div>
          </div>
        </div>
      </Async.Success>
    </Async.Root>
  );
};

export default EntityFormEngine;
